import React, { useEffect, useMemo } from "react";
import {
	Button,
	Dropdown,
	DropdownMenuButton,
	IconButton,
	Loading,
} from "@alm/flow-ds/components";
import Element from "../Element";
import useForm from "hooks/useForm";
import useLinkedForm from "hooks/useLinkedForm";
import { WorkbenchLayer } from "components/Workbench";
import { WorkbenchContext } from "contexts/WorkbenchContext";
import TaskPageTemplate from "components/Templates/TaskPageTemplate";
import PageHeader from "components/PageHeader";
import LinkedForm from "./LinkedForm";
import { useTranslation } from "react-i18next";

function Form({
	fid,
	workbenchIndex,
}: {
	fid: string;
	workbenchIndex: number;
}) {
	const { t } = useTranslation();

	// Get the current form
	const { ready, error, uuid, label, save, canSave } = useForm(fid);

	// Get linked form
	const linked = useLinkedForm(fid, 0);

	/**
	 * Load the child form if there is a linked form
	 */
	useEffect(() => {
		if (ready && !linked.init && linked.hasChild) {
			linked.loadChild();
		}
	}, [linked, linked.hasChild, ready]);

	// Index of the current form
	const wbContext = useMemo(() => {
		return {
			workbenchIndex,
		};
	}, [workbenchIndex]);

	if (error) {
		console.error(error);
		return null;
	}

	if (!ready) return <Loading className="elem__loader--lg" />;

	return (
		<>
			<WorkbenchLayer>
				<PageHeader
					title={label || ""}
					className="page-header__title container">
					{/* Small screen action dropdown */}
					<Dropdown
						className="d-md-none"
						id="activityForm__dropdown"
						menuBtn={
							<IconButton
								className="btn-basic--default"
								icon="ellipsis-v"
								label={t(
									"main.icons.menuOptions"
								)}></IconButton>
						}
						align="right">
						<DropdownMenuButton
							disabled={!canSave}
							onClick={save}
							label={t("common.labels.save")}
						/>
					</Dropdown>

					{/* Large screen action buttons */}
					<div className="page-header__actions d-none d-md-block">
						<div className="page-header__actions-buttons">
							<Button
								className="btn-primary"
								disabled={!canSave}
								onClick={save}
								label={t("common.labels.save")}
							/>
						</div>
					</div>
				</PageHeader>
				<div className="page-content-container container mt-1">
					<div className="page-content">
						<WorkbenchContext.Provider value={wbContext}>
							<TaskPageTemplate>
								<header>
									<h2 className="form__label">{label}</h2>
								</header>

								<Element rowIndex={0} fid={fid} uuid={uuid} />
							</TaskPageTemplate>
						</WorkbenchContext.Provider>
					</div>
				</div>
				{ready && (
					<div className="page-footer workbench-footer container visible d-md-none">
						<Button
							className="btn-primary workbench-footer__action-btn btn-lg"
							disabled={!canSave}
							onClick={save}
							label={t("common.labels.save")}
						/>
					</div>
				)}
			</WorkbenchLayer>

			{linked.init && (
				<WorkbenchLayer>
					{linked.child ? (
						<LinkedForm
							fid={linked.child}
							workbenchIndex={workbenchIndex + 1}
							breadcrumb={[{ name: label }]}
						/>
					) : (
						<div className="workbench-layer__editor">
							<Loading className="elem__loader--lg mt-5" />
						</div>
					)}
				</WorkbenchLayer>
			)}
		</>
	);
}

export default React.memo(Form);
